import Button from '@doordash/component-button'
import { Text, StackChildren, Colors, Inline } from '@doordash/design-language'
import * as React from 'react'
import { useState } from 'react'
import verifyIdentityStrings from './strings'
import PersonaClient, {
  PersonaClientCallbackProps,
} from '../../molecules/persona-client'
import { VehicleType, DASHER_APPLICANT_FAQ_URL } from '../../../constants'
import { IDVStatus } from '../../../utilities/steps'
import Tag from '@doordash/component-tag'
import { TagTypes } from '@doordash/component-tag/src/types/TagTypes'
import i18n from '../../../i18n'
import { SupportedLanguages } from '@doordash/lib-i18n'

type VerifyIdentityStepProps = {
  uniqueLink?: string
  vehicleType?: VehicleType
  idvStatus?: IDVStatus
  identityVerificationStatus?: IDVStatus
  identityInquiryId?: string
}

type VerifyIdentityStepSpecifics = {
  verifyIdentityStepDescriptions: string[]
  tagType: TagTypes
  tagText: string
  showFAQ: boolean
}

const pendingReviewStatuses = [IDVStatus.PENDING, IDVStatus.NEEDS_REVIEW]

const hardFailStatuses = [IDVStatus.FAILED, IDVStatus.DECLINED]

const getVerifyIdentityStepSpecifics = (
  idvStatus: IDVStatus,
  identityVerificationStatus: IDVStatus
): VerifyIdentityStepSpecifics => {
  if (
    pendingReviewStatuses.includes(idvStatus) ||
    pendingReviewStatuses.includes(identityVerificationStatus)
  ) {
    return {
      verifyIdentityStepDescriptions: [
        verifyIdentityStrings.progressionCheckVerifyIdentityUnderReviewDescription(),
      ],
      tagType: Tag.Types.WarningEmphasis,
      tagText: verifyIdentityStrings.progressionCheckVerifyIdentityUnderReview(),
      showFAQ: true,
    }
  } else if (
    hardFailStatuses.includes(idvStatus) ||
    hardFailStatuses.includes(identityVerificationStatus)
  ) {
    return {
      verifyIdentityStepDescriptions: [
        verifyIdentityStrings.progressionCheckVerifyIdentityUnableToVerifyDescription(),
        verifyIdentityStrings.progressionCheckVerifyIdentityUnableToVerifyFAQDescription(),
      ],
      tagType: Tag.Types.InformationalDefault,
      tagText: verifyIdentityStrings.progressionCheckVerifyIdentityUnableToVerify(),
      showFAQ: true,
    }
  }
  return {
    verifyIdentityStepDescriptions: [
      verifyIdentityStrings.progressionCheckVerifyIdentityDescription(),
    ],
    tagType: undefined,
    tagText: '',
    showFAQ: false,
  }
}

// Dasher support help links use a mix of languages and locales
// for their language param
const webParamToSupportedLanguagesMap = {
  en_US: [SupportedLanguages.EN],
  en_AU: [SupportedLanguages.EN_AU],
  es: [
    SupportedLanguages.ES,
    SupportedLanguages.ES_MX,
    SupportedLanguages.ES_US,
  ],
  fr_CA: [SupportedLanguages.FR],
  ja: [SupportedLanguages.JA, SupportedLanguages.JA_JP],
}

const getLocalizedFAQLink = (language: string): string => {
  const languageWebParamValue = Object.keys(
    webParamToSupportedLanguagesMap
  ).find(possibleWebParamValue =>
    webParamToSupportedLanguagesMap[possibleWebParamValue].includes(language)
  )
  return `${DASHER_APPLICANT_FAQ_URL}?language=${languageWebParamValue}`
}

const VerifyIdentityStep: React.FC<VerifyIdentityStepProps> = ({
  uniqueLink,
  vehicleType,
  idvStatus,
  identityInquiryId,
  identityVerificationStatus,
}) => {
  const [hasTriggeredPersonaClient, setHasTriggeredPersonaClient] = useState(
    false
  )

  const activateModal = e => {
    e.stopPropagation()
    setHasTriggeredPersonaClient(true)
  }

  const handlePersonaClientUpdate = (
    personaClientCallbackProps: PersonaClientCallbackProps
  ) => {
    if (
      personaClientCallbackProps &&
      personaClientCallbackProps.triggerPersonaClientRender != null
    ) {
      setHasTriggeredPersonaClient(
        personaClientCallbackProps.triggerPersonaClientRender
      )
    }
  }

  const {
    verifyIdentityStepDescriptions,
    tagText,
    showFAQ,
    tagType,
  } = getVerifyIdentityStepSpecifics(idvStatus, identityVerificationStatus)

  const language = i18n.getCurrentLanguage()
  const FAQ_URL = getLocalizedFAQLink(language)

  return (
    <React.Fragment>
      <StackChildren>
        {tagType && <Tag type={tagType} text={tagText} />}
        {verifyIdentityStepDescriptions.map(
          verifyIdentityStepDescriptionString => (
            <Text color={Colors.SystemGrey80}>
              {verifyIdentityStepDescriptionString}
            </Text>
          )
        )}
        <div>
          <Inline tag={'span'} size={Inline.Sizes.XxSmall}>
            {showFAQ ? (
              <Button
                size={Button.Sizes.Small}
                href={FAQ_URL}
                isInline
                tag={'a'}
              >
                {verifyIdentityStrings.progressionCheckVerifyIdentityFAQButtonText()}
              </Button>
            ) : (
              <Button
                size={Button.Sizes.Small}
                tag="a"
                onClick={e => {
                  activateModal(e)
                }}
                isInline
              >
                {identityInquiryId
                  ? verifyIdentityStrings.progressionCheckVerifyIdentityInProgressButtonText()
                  : verifyIdentityStrings.progressionCheckVerifyIdentityButtonText()}
              </Button>
            )}
          </Inline>
        </div>
      </StackChildren>
      <PersonaClient
        referenceId={uniqueLink}
        vehicleType={vehicleType}
        triggerPersonaClientRender={hasTriggeredPersonaClient}
        onUpdate={handlePersonaClientUpdate}
      />
    </React.Fragment>
  )
}

export default VerifyIdentityStep
